import { useForm } from 'react-hook-form';
import {
  useCallback, useEffect, useState, useMemo, useRef
} from 'react';
import { find, isEmpty, isString } from 'lodash';
import { useDispatch } from 'react-redux';
import { geocoderLocationInfo } from '../../../../util/utils';
import { SIDEBAR_ACTIONS } from '../../../../constants/actionsConstants';

/**
 * Hook para manejo del formulario de direcciones de cliente.
 *
 * @hook useCustomerAddressForm
 * @category Hook - Forms Layer
 * @description
 * - Gestiona el estado del formulario de direcciones con react-hook-form.
 * - Integra Google Maps API y Autocomplete para selección de direcciones.
 * - Maneja geocodificación inversa desde coordenadas.
 * - Carga y valida datos de direcciones existentes.
 * - Controla modal de selección de dirección en mapa.
 *
 * @param {Object} params - Parámetros del hook
 * @param {Object} params.selectedAddress - Dirección seleccionada para editar (opcional)
 * @param {boolean} params.loadLocation - Si debe cargar la ubicación automáticamente
 * @param {Array<Object>} params.countryOptions - Opciones de países disponibles
 *
 * @returns {Object} Estado y métodos del formulario
 * @returns {Object} returns.formMethods - Métodos de react-hook-form
 * @returns {boolean} returns.isLoaded - Si el hook está cargado completamente
 * @returns {boolean} returns.googleMapsApiLoaded - Si Google Maps API está cargada
 * @returns {Object|null} returns.autoComplete - Instancia de Google Autocomplete
 * @returns {boolean} returns.errorMain - Error de validación de dirección principal
 * @returns {Function} returns.setErrorMain - Setter para error de dirección principal
 * @returns {Function} returns.onLoadAutocomplete - Handler para carga de autocomplete
 * @returns {Function} returns.onPlaceChanged - Handler para cambio de lugar en autocomplete
 * @returns {Function} returns.openSelectAddressModal - Abre modal de selección en mapa
 * @returns {Function} returns.fillFormData - Llena formulario con datos de dirección
 * @returns {boolean} returns.openedMapPlace - Si modal de mapa está abierto
 * @returns {Object} returns.addressData - Datos de dirección actual
 * @returns {Function} returns.closeSelectAddressModal - Cierra modal de selección en mapa
 * @returns {boolean} returns.allRequiredFilled - Si todos los campos obligatorios están llenos
 *
 * @example
 * const {
 *   formMethods,
 *   isLoaded,
 *   googleMapsApiLoaded,
 *   onLoadAutocomplete,
 *   onPlaceChanged
 * } = useCustomerAddressForm({
 *   selectedAddress,
 *   loadLocation: true,
 *   countryOptions
 * });
 *
 * @since 4.0.0
 * @version 4.0.0 - Hook de forms layer para ManageCustomerAddressMC
 */

const useCustomerAddressForm = ({
  selectedAddress,
  loadLocation,
  countryOptions,
}) => {
  const dispatch = useDispatch();
  const [isLoaded, setIsLoaded] = useState(false);
  const [googleMapsApiLoaded, setGoogleMapsApiLoaded] = useState(false);
  const autoCompleteRef = useRef(null);
  const [errorMain, setErrorMain] = useState(false);
  const selectedSuggestedAddressRef = useRef('');

  const formMethods = useForm({
    defaultValues: {
      address: '',
      description: '',
      city: '',
      state: '',
      zip_code: '',
      country_id: '',
      selected_country: null,
      main: false,
      latitude: null,
      longitude: null
    }
  });

  const { setValue, getValues, watch } = formMethods;

  // Observar campos obligatorios para validación de botón
  const watchedAddress = watch('address');
  const watchedCity = watch('city');
  const watchedState = watch('state');
  const watchedSelectedCountry = watch('selected_country');

  const isFilled = (v) => !isEmpty(v) || (isString(v) && v.trim() !== '');

  const allRequiredFilled = useMemo(() => (
    isFilled(watchedAddress) &&
    isFilled(watchedCity) &&
    isFilled(watchedState) &&
    isFilled(watchedSelectedCountry)
  ), [watchedAddress, watchedCity, watchedState, watchedSelectedCountry]);

  // Verificar si Google Maps está cargado
  const checkGoogleMapsLoaded = useCallback(() => {
    if (typeof window !== 'undefined' &&
      window.google &&
      window.google.maps &&
      window.google.maps.places &&
      window.google.maps.places.Autocomplete) {
      setGoogleMapsApiLoaded(true);
      setIsLoaded(true);
      return true;
    }
    return false;
  }, []);

  // Llenar datos del formulario
  const fillFormData = useCallback((data) => {
    setValue('address', data.address, { shouldDirty: true });
    setValue('city', data.city, { shouldDirty: true });
    setValue('state', data.state, { shouldDirty: true });
    setValue('zip_code', data.zip_code, { shouldDirty: true });
    setValue('country_id', data.country_id, { shouldDirty: true });
    setValue('selected_country', data.country_id, { shouldDirty: true });
    setValue('latitude', data.latitude, { shouldDirty: true });
    setValue('longitude', data.longitude, { shouldDirty: true });
  }, [setValue]);

  // Cargar información de lugar desde coordenadas
  const loadPlace = useCallback((coordinates, preserveAddress = null) => {
    const geocoder = new window.google.maps.Geocoder();
    const newPosition = {
      lat: Number(coordinates.latitude),
      lng: Number(coordinates.longitude)
    };

    geocoder.geocode({ location: newPosition }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const info = geocoderLocationInfo(results);

        // Preservar la dirección completa si se proporciona
        if (preserveAddress) {
          info.address = preserveAddress;
        } else if (selectedSuggestedAddressRef.current) {
          info.address = selectedSuggestedAddressRef.current;
        } else {
          // Preservar el address existente si ya hay uno en el formulario y no cambio lat y lng
          const addressValue = getValues('address');
          const formLat = getValues('latitude');
          const formLng = getValues('longitude');
          if (
            addressValue &&
            Number(formLat) === Number(coordinates.latitude) &&
            Number(formLng) === Number(coordinates.longitude)
          ) {
            info.address = addressValue;
          }
        }

        fillFormData(info);
        selectedSuggestedAddressRef.current = '';
      } else {
        console.error('No existe dirección');
      }
    });
  }, [fillFormData, getValues]);

  // Cargar script de Google Maps
  useEffect(() => {
    if (checkGoogleMapsLoaded()) return;

    const loadScript = () => {
      if (document.querySelector(`script[src="https://maps.googleapis.com/maps/api/js?key=${ENV.GOOGLE_API_KEY}&libraries=places"]`)) {
        const checkInterval = setInterval(() => {
          if (checkGoogleMapsLoaded()) {
            clearInterval(checkInterval);
          }
        }, 100);
        setTimeout(() => clearInterval(checkInterval), 10000);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${ENV.GOOGLE_API_KEY}&libraries=places`;
      script.async = true;
      script.onload = () => {
        setGoogleMapsApiLoaded(true);
        setIsLoaded(true);
      };
      document.head.appendChild(script);
    };

    loadScript();
  }, [checkGoogleMapsLoaded]);

  // Inicializar formulario con dirección existente o valores por defecto
  useEffect(() => {
    if (isEmpty(selectedAddress)) {
      setValue('country_id', ENV.COUNTRY_CODE);
      setValue('selected_country', find(countryOptions, { value: ENV.COUNTRY_CODE }));
      return;
    }

    Object.keys(selectedAddress).forEach((key) => {
      if (key === 'country_id') {
        setValue('selected_country', find(countryOptions, { value: selectedAddress[key] }));
      }
      setValue(key, selectedAddress[key]);
    });
  }, [setValue, selectedAddress, countryOptions]);

  // Cargar ubicación si está habilitado
  useEffect(() => {
    if (!loadLocation || !isLoaded || !googleMapsApiLoaded) return;

    if (selectedAddress && selectedAddress.latitude && selectedAddress.longitude) {
      loadPlace(selectedAddress);
    }
  }, [loadLocation, isLoaded, googleMapsApiLoaded, selectedAddress, loadPlace]);

  // Handlers para autocomplete
  const onLoadAutocomplete = useCallback((service) => {
    autoCompleteRef.current = service;
  }, []);

  const onPlaceChanged = useCallback(() => {
    const autoComplete = autoCompleteRef.current;
    if (isEmpty(autoComplete) || !autoComplete || typeof autoComplete.getPlace !== 'function') return;

    try {
      const place = autoComplete.getPlace();

      if (!place) return;

      if (!place.geometry || !place.geometry.location) {
        alert('Esta ubicación no está disponible');
        return;
      }

      if (place.geometry.viewport || place.geometry.location) {
        const fullAddress = place.formatted_address;

        // Establecer inmediatamente la dirección completa en el input
        setValue('address', fullAddress);

        const newPosition = {
          latitude: place.geometry?.location?.lat(),
          longitude: place.geometry?.location?.lng()
        };

        // Pasar la dirección completa para que se preserve
        loadPlace(newPosition, fullAddress);
      }
    } catch (error) {
      console.error('Error al obtener el lugar del Autocomplete:', error);
    }
  }, [setValue, loadPlace]);

  // Handler para abrir modal de selección de dirección
  const openSelectAddressModal = useCallback(() => {
    const addressData = getValues();
    dispatch({
      type: SIDEBAR_ACTIONS.OPEN_SIDEBAR,
      payload: {
        componentKey: 'LocationPickerSidebar',
        props: {
          handleSubmit: (data) => {
            // No pasar dirección completa desde el mapa, solo coordenadas
            loadPlace({ latitude: data.latitude, longitude: data.longitude });
          },
          defaultLocation:
            addressData.latitude && addressData.longitude
              ? { lat: Number(addressData.latitude), lng: Number(addressData.longitude) }
              : undefined
        },
      },
    });
  }, [getValues, loadPlace, dispatch]);

  // Cleanup cuando el componente se desmonta
  useEffect(() => {
    return () => {
      autoCompleteRef.current = null;
    };
  }, []);

  return useMemo(() => ({
    formMethods,
    isLoaded,
    googleMapsApiLoaded,
    autoComplete: autoCompleteRef.current,
    errorMain,
    setErrorMain,
    onLoadAutocomplete,
    onPlaceChanged,
    openSelectAddressModal,
    fillFormData,
    allRequiredFilled,
  }), [
    formMethods,
    isLoaded,
    googleMapsApiLoaded,
    errorMain,
    onLoadAutocomplete,
    onPlaceChanged,
    openSelectAddressModal,
    fillFormData,
    allRequiredFilled,
  ]);
};

export default useCustomerAddressForm;
